import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessengerMessageActionsPort} from '../message-actions.port';
import {MessageReceipt} from '../messenger.models';

export interface MessageInfoDialogData {
  messageId: string;
  /** The message's text, as the thread shows it. */
  preview: string;
  /** The sender, who is never listed as a reader of their own message. */
  senderEmail: string;
  /** ITP-E-249-05: a voice message, whose info also says who played it and when. */
  voice?: boolean;
}

/**
 * MESSAGE INFO (ITP-E-194-07, B-14). Who has read the message and when, who it reached and has not read it yet, and who
 * it has not reached, from `messageReceipts(messageId)` through the message actions port. Opened from the message menu
 * of the member's own message.
 *
 * ITP-E-249-05 (story 12, B-14): a voice message also lists who has played it and when (`playedAt`), above Read by. Each member is
 * listed once, under the furthest it has got: played, read, delivered, or not delivered yet. Any other message has no Played.
 */
@Component({
  standalone: false,
  selector: 'messenger-message-info-dialog',
  templateUrl: './message-info-dialog.component.html',
  styleUrls: ['./message-info-dialog.component.scss']
})
export class MessageInfoDialogComponent implements OnInit, OnDestroy {
  loading = true;
  failed = false;
  /** ITP-E-249-05: the members who have played the voice message, latest first. */
  playedBy: MessageReceipt[] = [];
  readBy: MessageReceipt[] = [];
  deliveredTo: MessageReceipt[] = [];
  waiting: MessageReceipt[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(private dialogRef: MatDialogRef<MessageInfoDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: MessageInfoDialogData,
              private messageActions: MessengerMessageActionsPort) {
  }

  ngOnInit(): void {
    const sender = (this.data.senderEmail || '').toLowerCase();
    this.messageActions.receipts(this.data.messageId).pipe(takeUntil(this.destroy$)).subscribe({
      next: receipts => {
        const others = (receipts || []).filter(receipt => (receipt.email || '').toLowerCase() !== sender);
        const played = (receipt: MessageReceipt) => !!this.data.voice && !!receipt.playedAt;
        this.playedBy = MessageInfoDialogComponent.latestFirst(others.filter(played), 'playedAt');
        const notPlayed = others.filter(receipt => !played(receipt));
        this.readBy = MessageInfoDialogComponent.latestFirst(notPlayed.filter(receipt => !!receipt.readAt), 'readAt');
        this.deliveredTo = MessageInfoDialogComponent.latestFirst(
          notPlayed.filter(receipt => !receipt.readAt && !!receipt.deliveredAt), 'deliveredAt');
        this.waiting = notPlayed.filter(receipt => !receipt.readAt && !receipt.deliveredAt);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** The face's letters: the first of the first and last name, or of the email. */
  initials(receipt: MessageReceipt): string {
    const words = (receipt.name || receipt.email || '').trim().split(/\s+/).filter(word => !!word);
    return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
  }

  trackByEmail(index: number, receipt: MessageReceipt): string {
    return receipt.email;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private static latestFirst(receipts: MessageReceipt[], key: 'playedAt' | 'readAt' | 'deliveredAt'): MessageReceipt[] {
    return [...receipts].sort((a, b) => (b[key] || '').localeCompare(a[key] || ''));
  }
}
